/**
 * The admin's pagination bar (`.cl-pagination` in the prototype's
 * setup-rail.css).
 *
 * **Its own card below the table, not a footer inside it** — which is why this
 * is not `AdminPagedTable`: that composes MUI's `TablePagination` into the
 * table's bottom border, and the design draws a separate rounded bar with
 * "Showing X–Y of N" on the left and the rows-per-page select, page info and
 * arrows on the right.
 *
 * Built for the Contests list by CAE-1542 and lifted into the kit by CAE-1556,
 * which needed the same bar under Users, Operations, Configuration and the
 * prize-tier defaults. Those screens were each rendering MUI's control welded
 * into the table border — the same facts in a different voice, two rows below
 * a redesigned screen that said them this way.
 *
 * **It derives the sentence from `page` / `rowsPerPage` / `total`** rather than
 * taking `from` and `to`. Six callers computing the same arithmetic is six
 * chances to get the empty list or the short last page wrong, and both are
 * easy to get wrong: the range of nothing is 0–0, not 1–0.
 */
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import { ChevronLeftIcon, ChevronRightIcon } from './line-icons';
import { formatInteger } from '../../utils/number';

/**
 * `.cl-pag-rowsel select`'s three options, which is what the design draws.
 *
 * Not the admin's usual four: 100 is absent. A screen that pages on the server
 * passes `ADMIN_TABLE_ROWS_PER_PAGE_OPTIONS` instead, where the page size is a
 * real request parameter rather than a slice of rows already in hand.
 */
export const ADMIN_LIST_PAGE_SIZES = [10, 25, 50] as const;

/** What the "Showing … of N" sentence is counting, in both numbers. */
export interface PaginatedEntity {
  one: string;
  many: string;
}

/**
 * Whether a list is long enough to be worth a pagination bar at all.
 *
 * **Not `total > rowsPerPage`**, which is the obvious test and a trap: an
 * operator who sets the page size to 50 over a 24-row list makes the bar
 * disappear, and with it the only control that could set the size back. The
 * list is still correct — all 24 rows are on screen — but the screen has
 * quietly eaten its own affordance.
 *
 * Keyed on the *smallest* size on offer instead, so visibility depends on the
 * data rather than on a choice the operator just made. A list that could be
 * paged at any size keeps its bar at every size; one that fits in a single
 * page at the tightest setting never shows one, because "Page 1 of 1" beside
 * two dead arrows is chrome describing nothing.
 */
export const listNeedsPagination = (
  total: number,
  rowsPerPageOptions: readonly number[] = ADMIN_LIST_PAGE_SIZES,
): boolean => total > Math.min(...rowsPerPageOptions);

interface AdminListPaginationProps {
  /** Zero-based, as every caller's state is. */
  page: number;
  rowsPerPage: number;
  /** Rows across every page, not the ones on screen. */
  total: number;
  entity: PaginatedEntity;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: readonly number[];
}

/** The emphasised numbers inside the "Showing" and "Page" sentences. */
const Strong = ({ children, mono }: { children: React.ReactNode; mono?: boolean }) => (
  <Box
    component="strong"
    sx={(theme: Theme) => ({
      color: theme.tokens.color.ink2,
      fontWeight: 600,
      ...(mono ? { fontFamily: theme.tokens.font.mono, fontVariantNumeric: 'tabular-nums' } : {}),
    })}
  >
    {children}
  </Box>
);

const NavButton = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <ButtonBase
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    sx={(theme: Theme) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: `${theme.tokens.radius.control}px`,
      border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
      backgroundColor: theme.tokens.color.surface,
      color: theme.tokens.color.ink2,
      fontSize: '12px',
      transition: 'all 0.12s',
      '& svg': { fontSize: 'inherit' },
      '&:hover:not(:disabled)': {
        backgroundColor: theme.tokens.color.surfaceMuted,
        color: theme.tokens.color.ink,
        borderColor: theme.tokens.componentColor.controlHoverBorder,
      },
      '&:disabled': { opacity: 0.38, cursor: 'not-allowed' },
      '&:focus-visible': {
        outline: `2px solid ${theme.tokens.color.accent}`,
        outlineOffset: '2px',
      },
    })}
  >
    {children}
  </ButtonBase>
);

/**
 * Where the bar's numbers come from.
 *
 * One page, never zero: "Page 1 of 0" is not a thing, and a screen hides the
 * bar on an empty list anyway. The range of an empty list is 0–0 rather than
 * 1–0, because there is no first row to be showing.
 */
export const paginationRange = (page: number, rowsPerPage: number, total: number) => {
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const safePage = Math.min(Math.max(0, page), pageCount - 1);
  const start = safePage * rowsPerPage;
  const shown = Math.max(0, Math.min(rowsPerPage, total - start));

  return {
    page: safePage,
    pageCount,
    from: shown === 0 ? 0 : start + 1,
    to: start + shown,
  };
};

export const AdminListPagination = ({
  page,
  rowsPerPage,
  total,
  entity,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = ADMIN_LIST_PAGE_SIZES,
}: AdminListPaginationProps) => {
  const range = paginationRange(page, rowsPerPage, total);

  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
        padding: '14px 18px',
        backgroundColor: theme.tokens.color.surface,
        border: theme.tokens.border.default,
        borderRadius: `${theme.tokens.radius.card}px`,
        boxShadow: theme.tokens.shadow.card,
        fontSize: '13px',
        color: theme.tokens.color.ink3,
      })}
    >
      <Box component="span">
        Showing{' '}
        <Strong>
          {range.from}–{range.to}
        </Strong>{' '}
        of <Strong>{formatInteger(total)}</Strong> {total === 1 ? entity.one : entity.many}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <Box
          component="label"
          sx={(theme: Theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12.5px',
            color: theme.tokens.color.ink3,
          })}
        >
          <Box component="span">Rows per page</Box>
          {/*
            A native `<select>`, as the design draws it — and the one control on
            these screens where that is the better answer anyway: it is three
            numbers, and the platform's own picker needs no keyboard handling of
            ours.
          */}
          <Box
            component="select"
            value={rowsPerPage}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              onRowsPerPageChange(Number(event.target.value));
              /*
                And back to the first page, as `AdminPagedTableFooter` did.
                Without it, an operator on page 4 of 42 who picks "25" fires a
                request at `offset: 75` — past the end, so an empty page — and
                is then pulled to page 2 by the clamp rather than page 1.
              */
              onPageChange(0);
            }}
            sx={(theme: Theme) => ({
              font: 'inherit',
              fontSize: '13px',
              color: theme.tokens.color.ink,
              backgroundColor: theme.tokens.color.surface,
              border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
              borderRadius: `${theme.tokens.radius.control}px`,
              padding: '5px 8px',
              cursor: 'pointer',
              '&:hover': { borderColor: theme.tokens.componentColor.controlHoverBorder },
            })}
          >
            {rowsPerPageOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
          <NavButton
            label="Previous page"
            disabled={range.page <= 0}
            onClick={() => onPageChange(range.page - 1)}
          >
            <ChevronLeftIcon />
          </NavButton>
          <Box component="span" sx={{ fontSize: '12.5px' }}>
            Page <Strong mono>{range.page + 1}</Strong> of <Strong mono>{range.pageCount}</Strong>
          </Box>
          <NavButton
            label="Next page"
            disabled={range.page >= range.pageCount - 1}
            onClick={() => onPageChange(range.page + 1)}
          >
            <ChevronRightIcon />
          </NavButton>
        </Box>
      </Box>
    </Box>
  );
};
